import { ProductService } from './service';
import { ProductsQueryParams } from './types';

/**
 * Maximum number of distinct product IDs to request per page.
 * Higher than fetchAllFlatProducts' EXPORT_PAGE_SIZE (5000) since this
 * payload is IDs only — no Cartesian manufacturer/facility/grade/
 * therapeutic-area rows to inflate response size.
 */
const IDS_PAGE_SIZE = 5000;

/**
 * Hard ceiling on total distinct product IDs fetched, mirrors
 * fetchAllFlatProducts' EXPORT_MAX_PRODUCTS guard against runaway pagination.
 */
const IDS_MAX_PRODUCTS = 25000;

export type ProductIdsFilterParams = Omit<
  ProductsQueryParams,
  'limit' | 'offset' | 'sortBy' | 'sortDirection'
>;

export interface FetchAllProductIdsOptions {
  /** AbortSignal — cancel the in-flight request immediately. */
  signal?: AbortSignal;
}

/**
 * Fetches ALL product IDs matching the current filter by iterating through
 * pages of the /api/products/ids endpoint (issue #637, VPE-010 sub-task
 * 10.2) — mirrors fetchAllFlatProducts' all-pages-until-short-page loop, IDs
 * only. Used by the validate-products devtool modal (Sub-task 9.3) to
 * target every product matching the grid's current filter, across all
 * pages, not just the current page.
 *
 * @param service  An instantiated ProductService (caller owns auth/context).
 * @param params   Current filter params (search / advancedSearch only).
 * @param options  Optional abort signal.
 * @returns        Flat array of all matching product_ids across all pages.
 */
export async function fetchAllProductIds(
  service: ProductService,
  params: ProductIdsFilterParams,
  options: FetchAllProductIdsOptions = {}
): Promise<number[]> {
  const { signal } = options;
  const allIds: number[] = [];
  let offset = 0;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Fetch cancelled', 'AbortError');
    }

    const response = await service.getIds(
      { ...params, limit: IDS_PAGE_SIZE, offset },
      { signal }
    );

    const { product_ids: ids } = response.data;
    allIds.push(...ids);

    if (ids.length < IDS_PAGE_SIZE) {
      break;
    }

    offset += ids.length;

    if (offset >= IDS_MAX_PRODUCTS) {
      console.warn(
        `[fetchAllProductIds] Ceiling reached: ${IDS_MAX_PRODUCTS} product IDs fetched. Some records may be omitted.`
      );
      break;
    }
  }

  return allIds;
}
